"use client";

import createGlobe from "cobe";
import { useCallback, useEffect, useRef, useState } from "react";

const THEMED_GLOBE_CONFIG = {
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 1,
  diffuse: 1.1,
  mapSamples: 16000,
  mapBrightness: 1,
  baseColor: [0.35, 0.49, 0.81],
  markerColor: [0, 174 / 255, 239 / 255],
  glowColor: [164 / 255, 77 / 255, 228 / 255],
  markers: [
    { location: [14.5995, 120.9842], size: 0.03 },
    { location: [19.076, 72.8777], size: 0.1 },
    { location: [23.8103, 90.4125], size: 0.05 },
    { location: [30.0444, 31.2357], size: 0.07 },
    { location: [39.9042, 116.4074], size: 0.08 },
    { location: [-23.5505, -46.6333], size: 0.1 },
    { location: [19.4326, -99.1332], size: 0.1 },
    { location: [40.7128, -74.006], size: 0.1 },
    { location: [34.6937, 135.5022], size: 0.05 },
    { location: [41.0082, 28.9784], size: 0.06 },
  ],
};

export default function Globe({ config = THEMED_GLOBE_CONFIG }) {
  const phiRef = useRef(0); // ✅ persistent phi
  const [width, setWidth] = useState(0);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pointerInteracting = useRef(null);
  const pointerInteractionMovement = useRef(0);
  const [r, setR] = useState(0);
  const globeRef = useRef(null);

  const updatePointerInteraction = (value) => {
    pointerInteracting.current = value;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = value ? "grabbing" : "grab";
    }
  };

  const updateMovement = (clientX) => {
    if (pointerInteracting.current !== null) {
      const delta = clientX - pointerInteracting.current;
      pointerInteractionMovement.current = delta;
      setR(delta / 200);
    }
  };

  const onRender = useCallback(
    (state) => {
      if (!pointerInteracting.current) {
        phiRef.current += 0.005; // ✅ mutate ref safely
      }
      state.phi = phiRef.current + r;
      state.width = width * 2;
      state.height = width * 2;
    },
    [r, width]
  );

  const resize = () => {
    if (containerRef.current) {
      const newWidth = containerRef.current.offsetWidth;
      setWidth(newWidth);
    }
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    if (!width || !canvasRef.current) return;

    if (globeRef.current) globeRef.current.destroy();

    globeRef.current = createGlobe(canvasRef.current, {
      ...config,
      width: width * 2,
      height: width * 2,
      onRender,
    });

    setTimeout(() => {
      if (canvasRef.current) canvasRef.current.style.opacity = "1";
    });

    return () => globeRef.current?.destroy();
  }, [width, onRender, config]);

  return (
    <div ref={containerRef} className="globe-container">
      <canvas
        className="globe-canvas"
        ref={canvasRef}
        onPointerDown={(e) =>
          updatePointerInteraction(
            e.clientX - pointerInteractionMovement.current
          )
        }
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) =>
          e.touches[0] && updateMovement(e.touches[0].clientX)
        }
      />
    </div>
  );
}
